import { Promotion } from 'reducers/playerPromos';

export type PromoTier = {
  threshold: number;
  label: string;
  valueLabel: string;
  reached: boolean;
};

export type PromoDisplay = {
  format: string;
  formatLabel: string;
  metricLabel: string;
  progressLabel: string;
  progressValue: number;
  targetValue: number;
  progressPct: number;
  earnedLabel: string;
  statusLabel: string;
  resetDayLabel: string;
  resetLabel: string;
  endLabel: string;
  tiers: PromoTier[];
  detailRows: { label: string; value: string }[];
  weekRows: {
    key: string;
    label: string;
    dateLabel: string;
    value: string;
    rewardLabel: string;
    status: string;
    reached: boolean;
    /** Cash back: the window "today" falls in. */
    isCurrent?: boolean;
    /** Cash back: a window that has not started yet. */
    isUpcoming?: boolean;
    /** Cash back: the window's dates, e.g. "Nov 8–14". */
    rangeLabel?: string;
    /** Cash back: this window reached the top tier ($50). */
    topTier?: boolean;
    /** Hours played in the window as "7h 36m", rounded down (cash back). */
    playedLabel?: string;
  }[];
  /** Two-tier formats (weekly_cash_back): the tier this window has reached so far. */
  tierState?: 'notQualified' | 'qualified' | 'bonusAchieved';
  /** Time to the NEXT unreached tier, to the minute ("7h 24m"). Surfaces prefer it over
   *  target minus progress, which would count to the TOP tier (the EN-10913 lesson). */
  remainingLabel?: string;
  /** Two-tier formats: the next tier once the first is earned ("Next: $50 at 30h"); empty
   *  before the first tier and after the top one, so status rows never say the same thing twice. */
  nextTierLabel?: string;
  /** Two-tier formats: cash earned across every window of the promo so far ("$100"), or
   *  empty when nothing is earned yet. The reward card shows this, not the week's tier. */
  monthTotalLabel?: string;
  eligibleBlindsLabel?: string;
  weekWindowLabel?: string;
  qualifyingDateRangeLabel?: string;
  hybridDetails?: {
    state: 'notQualified' | 'qualified' | 'bonusAchieved';
    entryStatusKey:
      | 'notQualified'
      | 'currentWeekQualified'
      | 'weeklyPathQualified'
      | 'totalFallbackQualified';
    remainingLabel: string;
    currentWeekHoursLabel: string;
    weeklyTargetHoursLabel: string;
    totalHoursLabel: string;
    totalTargetHoursLabel: string;
    eligibleBlindsLabel: string;
    weekWindowLabel: string;
    resetDayLabel: string;
    tournamentDateLabel: string;
    qualifyingDateRangeLabel: string;
    seatLabel: string;
    startingStackLabel: string;
    bonusStackLabel: string;
    totalFallbackAvailable: boolean;
    totalFallbackQualified: boolean;
    currentWeekQualified: boolean;
    totalFallbackStackLabel: string;
  };
};

const FORMAT_WEEKLY = 'weekly_qualification_freeroll';
const FORMAT_TIER = 'tournament_tier_qualification';
const FORMAT_HYBRID = 'hybrid_freeroll_qualification';
const FORMAT_TIMEBACK = 'weekly_timeback_credit';
// Weekly Cash Back, the format EN-11292 adds in Admin. Designed in EN-11291; EN-11294 builds
// on everything keyed on it below.
const FORMAT_CASH_BACK = 'weekly_cash_back';

const formatLabels: Record<string, string> = {
  [FORMAT_WEEKLY]: 'Weekly Freeroll',
  [FORMAT_TIER]: 'Tier Qualification',
  [FORMAT_HYBRID]: 'Hybrid Freeroll',
  [FORMAT_TIMEBACK]: 'Weekly Timeback',
  [FORMAT_CASH_BACK]: 'Weekly Cash Back',
  legacy: 'Promotion'
};

const resetDayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fullDayLabels = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const toNumber = (value: any, fallback = 0): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const compactNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}K`;
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
};

const hoursLabel = (value: number): string =>
  `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}h`;

const moneyLabel = (value: number): string =>
  `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const chipsLabel = (value: number): string => `${compactNumber(value)} chips`;

const percent = (value: number, target: number): number => {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min((value / target) * 100, 100));
};

export const normalizeRules = (promo: Promotion): any => {
  const rules = promo.additional_rules;
  if (!rules) return {};
  if (typeof rules === 'object') return rules;

  try {
    // A stored "null" or scalar parses to a non-object; callers read fields off the result.
    const parsed = JSON.parse(rules);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const resolveFormat = (promo: Promotion, rules: any): string => {
  if (rules?.format) return rules.format;
  if (rules?.weeks || promo.goals?.type === 'weekly_hours') return FORMAT_WEEKLY;
  if (rules?.tiers && promo.goals?.type === 'weekly_net_fees') return FORMAT_TIMEBACK;
  if (rules?.tiers || promo.goals?.type === 'total_hours') return FORMAT_TIER;
  if (promo.goals?.type === 'hybrid_hours') return FORMAT_HYBRID;
  return 'legacy';
};

const sortedEntries = (object: any = {}): [string, any][] =>
  Object.entries(object || {}).sort(([left], [right]) => toNumber(left) - toNumber(right));

const sortedTiers = (object: any, valueFormatter: (value: any) => string): PromoTier[] =>
  sortedEntries(object).map(([threshold, value]) => ({
    threshold: toNumber(threshold),
    label: `${hoursLabel(toNumber(threshold))}`,
    valueLabel: valueFormatter(value),
    reached: false
  }));

const getWeekConfig = (promo: Promotion, rules: any): any => {
  const currentWeek = (promo.hours as any)?.current_week || promo.goals?.current_week;
  const ruleWeeks = rules?.weeks || {};
  if (currentWeek && ruleWeeks[currentWeek]) return ruleWeeks[currentWeek];

  const goalWeek = (promo.goals?.weeks || []).find((week: any) => week.week_key === currentWeek);
  if (goalWeek) {
    return {
      freeroll_tournament_id: goalWeek.freeroll_tournament_id,
      tiers: Object.fromEntries(
        (goalWeek.tiers || []).map((tier: any) => [tier.min_hours, tier.starting_chips])
      )
    };
  }

  const firstWeek = Object.keys(ruleWeeks).sort()[0];
  return firstWeek ? ruleWeeks[firstWeek] : null;
};

const getWeekConfigs = (promo: Promotion, rules: any): Record<string, any> => {
  const ruleWeeks = rules?.weeks || {};
  const goalWeeks = (promo.goals?.weeks || []).reduce((weeks: Record<string, any>, week: any) => {
    if (!week.week_key) return weeks;

    return {
      ...weeks,
      [week.week_key]: {
        freeroll_tournament_id: week.freeroll_tournament_id,
        tiers: Object.fromEntries(
          (week.tiers || []).map((tier: any) => [tier.min_hours, tier.starting_chips])
        )
      }
    };
  }, {});

  return {
    ...goalWeeks,
    ...ruleWeeks
  };
};

const getHighestReached = (tiers: PromoTier[], value: number): PromoTier | null => {
  return tiers.reduce<PromoTier | null>((best, tier) => {
    if (value < tier.threshold) return best;
    return !best || tier.threshold > best.threshold ? tier : best;
  }, null);
};

const getNextTier = (tiers: PromoTier[], value: number): PromoTier | null =>
  tiers.find((tier) => value < tier.threshold) || tiers[tiers.length - 1] || null;

const formatDate = (value?: string): string => {
  // Promo dates are Central wall-clock times the API labels as UTC ("2026-11-17T23:59:59.000Z"),
  // so read the calendar date as written: converting the instant shows Nov 18 in Manila.
  const date = parseDateOnly(value);
  return date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};

/**
 * Parse a promo date for display in the viewer's own timezone.
 *
 * A bare `YYYY-MM-DD` is parsed by `new Date()` as UTC midnight, so every
 * viewer west of UTC renders the PREVIOUS day — a tournament on the 21st shows
 * as "Dec. 20th" for a player in US Central. Build those from local parts
 * instead; timestamps that already carry a time keep their existing handling
 * (a space separator is normalised to `T` so Safari can parse it).
 */
const parseLocalDate = (value: string): Date | null => {
  const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTournamentDate = (value?: string): string => {
  if (!value) return '';
  const date = parseLocalDate(value);
  if (!date) return '';

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTournamentDateShort = (value?: string): string => {
  if (!value) return '';
  const date = parseLocalDate(value);
  if (!date) return '';

  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
      ? 'nd'
      : day % 10 === 3 && day !== 13
      ? 'rd'
      : 'th';

  return `${date.toLocaleDateString(undefined, { month: 'short' })}. ${day}${suffix}`;
};

const formatShortDate = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getMonth() + 1}/${date.getDate()}`;
};

// Rounds time still owed up to whole minutes so it never displays as 0:00 early. The
// tolerance absorbs float error such as 25 - 24.9 = 0.10000000000000142.
export const toRemainingMinutes = (hours: number): number =>
  Number.isFinite(hours) && hours > 0 ? Math.ceil(hours * 60 - 1e-6) : 0;

const formatMinutesClockLabel = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}H:${minutes.toString().padStart(2, '0')}M`;
};

const formatHoursClockLabel = (value: number): string =>
  formatMinutesClockLabel(Number.isFinite(value) ? Math.max(Math.round(value * 60), 0) : 0);

/** "7h 24m", "15h", "6m": the remaining-time shape the cash-back surfaces show. Rounds UP
 *  (toRemainingMinutes), so any time still left reads at least "1m", never "0m". */
const formatHoursMinutesLabel = (value: number): string => {
  const totalMinutes = toRemainingMinutes(value);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const formatStackLabel = (value: number): string => compactNumber(value);

const formatBlindLabel = (smallBlind: number): string => {
  if (!smallBlind || smallBlind <= 0) return '$0.25 / $0.50+';

  const bigBlind = smallBlind * 2;
  const formatBlind = (blind: number) =>
    `$${blind.toLocaleString(undefined, {
      minimumFractionDigits: blind < 1 ? 2 : 0,
      maximumFractionDigits: 2
    })}`;

  return `${formatBlind(smallBlind)} / ${formatBlind(bigBlind)}+`;
};

const getEligibleBlindsLabel = (rules: any): string =>
  rules.eligible_blinds_label ||
  rules.eligibleBlindsLabel ||
  formatBlindLabel(
    toNumber(rules.min_sb ?? rules.minimum_small_blind ?? rules.minimumSmallBlind, 0.25)
  );

// An explicit min_sb of 0 means every stake counts (charges.js skips only sb < min_sb).
const hasNoMinimumBlind = (rules: any): boolean =>
  !rules.eligible_blinds_label &&
  !rules.eligibleBlindsLabel &&
  toNumber(rules.min_sb ?? rules.minimum_small_blind ?? rules.minimumSmallBlind, 0.25) <= 0;

const getWeekWindowLabel = (resetDay: number): string => {
  const normalizedResetDay = resetDay >= 0 && resetDay <= 6 ? resetDay : 6;
  const startDay = fullDayLabels[normalizedResetDay] || 'Saturday';
  const endDay = fullDayLabels[(normalizedResetDay + 6) % 7] || 'Friday';
  return `${startDay} 12:00 AM CT - ${endDay} 11:59 PM CT`;
};

export const getCurrentWeekEndDateLabel = (
  promo: Promotion,
  options: { padded?: boolean } = {}
): string => {
  const displayEndDate = getCurrentWeekDisplayEndDate(promo);
  if (!displayEndDate) return '';

  const month = displayEndDate.getMonth() + 1;
  const day = displayEndDate.getDate();

  if (options.padded) {
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  }

  return `${month}/${day}`;
};

/** The current window's short weekday name ("Sat"), clamped to the promotion's end date
 *  like getCurrentWeekEndDateLabel. Empty when the window can't be worked out. */
export const getCurrentWeekEndWeekdayLabel = (promo: Promotion): string => {
  const displayEndDate = getCurrentWeekDisplayEndDate(promo);
  return displayEndDate ? resetDayLabels[displayEndDate.getDay()] : '';
};

/** The current window's last day, or the promotion's end date when it ends sooner. */
const getCurrentWeekDisplayEndDate = (promo: Promotion): Date | null => {
  const rules = normalizeRules(promo);
  const resetDay = toNumber(rules.reset_day, 6);
  const currentWeek = (promo.hours as any)?.current_week || promo.goals?.current_week;
  const weekEndDate =
    getServerWindowLastDay(promo.cash_back?.window_ends_at) ||
    getWeekEndDate(currentWeek, resetDay, promo);
  if (!weekEndDate) return null;

  const promoEndDate = parseDateOnly(promo.end_date);
  return promoEndDate && promoEndDate < weekEndDate ? promoEndDate : weekEndDate;
};

/** The window's last CT calendar day from the server's end instant (the next window's
 *  00:00 CT): the CT date one millisecond earlier, as a local date for formatting. */
const getServerWindowLastDay = (windowEndsAt?: string | null): Date | null => {
  if (!windowEndsAt) return null;
  const endsAt = new Date(windowEndsAt);
  if (Number.isNaN(endsAt.getTime())) return null;
  return parseDateOnly(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(endsAt.getTime() - 1))
  );
};

const getWeekEndDate = (
  value: string | undefined,
  resetDay: number,
  promo?: Promotion
): Date | null => {
  const weekStartDate = parseDateOnly(value) || getCurrentWeekStartDate(resetDay, promo);
  if (!weekStartDate) return null;

  const startDay = resetDay >= 0 && resetDay <= 6 ? resetDay : 6;
  const daysSinceStart = (weekStartDate.getDay() - startDay + 7) % 7;
  weekStartDate.setDate(weekStartDate.getDate() - daysSinceStart + 6);
  return weekStartDate;
};

/** The date the current window started, from today's date in Central Time (promo windows
 *  run on CT, not the device's zone), stepping back to the reset day. */
const getCurrentWeekStartDate = (resetDay: number, promo?: Promotion): Date => {
  const date = parseDateOnly(todayKeyCT(promo)) as Date;

  const startDay = resetDay >= 0 && resetDay <= 6 ? resetDay : 6;
  const daysSinceStart = (date.getDay() - startDay + 7) % 7;
  date.setDate(date.getDate() - daysSinceStart);
  return date;
};

const parseDateOnly = (value?: string): Date | null => {
  if (!value) return null;

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    return new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    );
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getConfiguredTournamentDate = (promo: Promotion, rules: any, weekConfig?: any): string =>
  formatTournamentDate(
    rules.tournament_start_date ||
      rules.tournamentStartDate ||
      weekConfig?.tournament_start_date ||
      promo.reward?.weeks?.find(
        (week: any) => week.freeroll_tournament_id === weekConfig?.freeroll_tournament_id
      )?.tournament_start_date ||
      promo.reward?.tournament_start_date
  );

const getWeeklyRows = (promo: Promotion, rules: any, target: number) => {
  const weeklyHours = (promo.hours as any)?.breakdowns?.weekly_hours || {};
  const currentWeek = (promo.hours as any)?.current_week || promo.goals?.current_week;
  const weekConfigs = getWeekConfigs(promo, rules);
  const weekKeys = Array.from(
    new Set([...Object.keys(weekConfigs), ...Object.keys(weeklyHours)])
  ).sort();

  return weekKeys.map((weekKey, index) => {
    const tiers = sortedTiers(
      weekConfigs[weekKey]?.tiers || {},
      (chips) => `${compactNumber(toNumber(chips))} chips`
    );
    const weekTarget = tiers[tiers.length - 1]?.threshold || target;
    const weekData = weeklyHours[weekKey] || {};
    const hours = toNumber(weekData.totalHours);
    const rewards = toNumber(weekData.rewards);
    const reached = Boolean(weekData.isAchieved) || (weekTarget > 0 && hours >= weekTarget);
    const highest = getHighestReached(tiers, hours);
    const rewardLabel =
      rewards > 0
        ? chipsLabel(rewards)
        : highest?.valueLabel || tiers[0]?.valueLabel || 'Qualified';
    const label = weekKey === currentWeek ? `Current Week: ${index + 1}` : `Week ${index + 1}`;

    return {
      key: weekKey,
      label,
      dateLabel: formatDate(`${weekKey}T00:00:00`),
      value: hoursLabel(hours),
      rewardLabel: reached ? rewardLabel : '0',
      status: reached ? 'Qualified' : 'In progress',
      reached
    };
  });
};

const getWeeklyHoursValue = (weekData: any): number =>
  typeof weekData === 'object' && weekData !== null
    ? toNumber(weekData.totalHours)
    : toNumber(weekData);

/**
 * The clock the cash-back windows read. Production uses the real time; Storybook pins it per
 * fixture (the prototype stories set `now` from the fixture's current week) so a story shows
 * the same state on any day. Not for production code to override.
 */
export const promoClock: { now: (promo?: Promotion) => Date } = { now: () => new Date() };

/** Today's date in Central Time as YYYY-MM-DD: the promo windows run on CT, not the device's
 *  zone (a Pacific player at 11 PM Saturday is already in Sunday's CT window). */
const todayKeyCT = (promo?: Promotion): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(promoClock.now(promo));

/** The CT date the current window started, as YYYY-MM-DD. */
const currentWeekStartKeyCT = (resetDay: number, promo?: Promotion): string =>
  toDateKey(getCurrentWeekStartDate(resetDay, promo));

const toDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

/** Every window the promotion schedules, keyed by the date it starts: the first reset-day
 *  date on or after the start date, then every 7 days through the end date. Mirrors the
 *  engine's `weeklyResetDayIndex`, so windows nobody has played yet still have a key. */
const getScheduledWeekKeys = (promo: Promotion, resetDay: number): string[] => {
  const start = parseDateOnly(promo.start_date);
  const end = parseDateOnly(promo.end_date);
  if (!start || !end || start > end) return [];
  const cursor = new Date(start);
  cursor.setDate(cursor.getDate() + ((resetDay - cursor.getDay() + 7) % 7));
  const keys: string[] = [];
  while (cursor <= end && keys.length < 60) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return keys;
};

type CashBackWeekResult = { hours: number; tier: (PromoTier & { amount: number }) | null };

type CashBackTierProgress = {
  tiers: PromoTier[];
  hours: number;
  highest: PromoTier | null;
  next: PromoTier | null;
  hoursToNext: number;
  /** Each window's hours and the tier it reached, keyed by the date it starts. */
  weekResults: Record<string, CashBackWeekResult>;
  /** The server listed its windows, so `current_week` adds none of its own. */
  scheduleFromPayload: boolean;
};

/**
 * Where a cash-back promotion stands. Reads the server's `cash_back` block (EN-11293), so
 * the dollar amounts, the tier reached and the hours to the next one are the server's. A
 * backend that predates it gets the same figures computed from the rules and the hours.
 */
const getCashBackTierProgress = (
  promo: Promotion,
  rules: any,
  thisWeekHours: number
): CashBackTierProgress => {
  const payload = promo.cash_back;

  if (payload && Array.isArray(payload.tiers)) {
    const reachedHours = payload.reached_tier ? toNumber(payload.reached_tier.min_hours) : -1;
    const tiers: PromoTier[] = payload.tiers.map((tier) => ({
      threshold: toNumber(tier.min_hours),
      label: hoursLabel(toNumber(tier.min_hours)),
      valueLabel: moneyLabel(toNumber(tier.credit_amount)),
      reached: toNumber(tier.min_hours) <= reachedHours
    }));
    const tierAt = (tier: { min_hours: number } | null) =>
      tier
        ? tiers.find((candidate) => candidate.threshold === toNumber(tier.min_hours)) || null
        : null;
    const weeks = payload.weeks || [];

    return {
      tiers,
      hours: toNumber(payload.this_week_hours),
      highest: tierAt(payload.reached_tier),
      next: tierAt(payload.next_tier),
      hoursToNext: toNumber(payload.hours_to_next_tier),
      weekResults: Object.fromEntries(
        weeks.map((week) => {
          const tier = tierAt(week.reached_tier);
          return [
            week.week_key,
            {
              hours: toNumber(week.hours),
              tier: tier && { ...tier, amount: toNumber(week.reached_tier?.credit_amount) }
            }
          ];
        })
      ),
      scheduleFromPayload: weeks.length > 0
    };
  }

  const tiers = sortedTiers(rules.tiers || {}, (amount) => moneyLabel(toNumber(amount))).map(
    (tier) => ({ ...tier, reached: thisWeekHours >= tier.threshold })
  );
  const next = tiers.find((tier) => thisWeekHours < tier.threshold) || null;
  const weeklyHours = (promo.hours as any)?.breakdowns?.weekly_hours || {};

  return {
    tiers,
    hours: thisWeekHours,
    highest: getHighestReached(tiers, thisWeekHours),
    next,
    hoursToNext: next ? next.threshold - thisWeekHours : 0,
    weekResults: Object.fromEntries(
      Object.entries(weeklyHours).map(([weekKey, weekData]) => {
        const hours = getWeeklyHoursValue(weekData);
        const tier = getHighestReached(tiers, hours);
        return [
          weekKey,
          {
            hours,
            tier: tier && { ...tier, amount: toNumber((rules.tiers || {})[tier.threshold]) }
          }
        ];
      })
    ),
    scheduleFromPayload: false
  };
};

/** One row per qualifying window for the cash-back progress modal: the window's hours and
 *  the cash it earned (the highest tier reached, never the sum). The rows come from the
 *  promotion's schedule merged with the windows the server or the tracked hours list, so a
 *  new player, or a payload that lists only played windows, still sees all four. */
const getCashBackWeekRows = (
  promo: Promotion,
  tiers: PromoTier[],
  currentWeek: string | undefined,
  resetDay: number,
  weekResults: Record<string, CashBackWeekResult>,
  scheduleFromPayload: boolean
) => {
  // Before the promotion starts every window is upcoming, whatever current_week says: the
  // server reports the first window as current until then.
  const startKey = (promo.start_date || '').slice(0, 10);
  const notStartedYet = Boolean(startKey) && todayKeyCT(promo) < startKey;
  // The schedule always lists every window (EN-11294); the payload's weeks only add to it.
  const scheduledKeys = getScheduledWeekKeys(promo, resetDay);
  const weekKeys = Array.from(
    new Set([
      ...scheduledKeys,
      ...Object.keys(weekResults),
      // A current week outside the schedule (e.g. today, before the promo starts) adds no
      // window; it only counts when there is no schedule to go by.
      ...(currentWeek && scheduledKeys.length === 0 && !scheduleFromPayload ? [currentWeek] : [])
    ])
  ).sort();
  const currentIndex = currentWeek && !notStartedYet ? weekKeys.indexOf(currentWeek) : -1;
  const beforeStart =
    notStartedYet || (currentIndex < 0 && currentWeek !== undefined && currentWeek < weekKeys[0]);

  return weekKeys.map((weekKey, index) => {
    const hours = weekResults[weekKey]?.hours ?? 0;
    const highest = weekResults[weekKey]?.tier ?? null;
    const reached = Boolean(highest);
    const isCurrent = index === currentIndex;
    const isUpcoming = beforeStart || (currentIndex >= 0 && index > currentIndex);

    return {
      key: weekKey,
      label: isCurrent ? `Current Week: ${index + 1}` : `Week ${index + 1}`,
      dateLabel: formatDate(`${weekKey}T00:00:00`),
      value: hoursLabel(hours),
      rewardLabel: highest ? highest.valueLabel : '$0',
      status: reached
        ? 'Earned'
        : isCurrent
        ? 'In progress'
        : isUpcoming
        ? 'Upcoming'
        : 'Not reached',
      reached,
      isCurrent,
      isUpcoming,
      rangeLabel: formatWeekRange(weekKey),
      playedLabel: formatPlayedLabel(hours),
      topTier: Boolean(highest) && highest?.threshold === tiers[tiers.length - 1]?.threshold
    };
  });
};

/** "7h 36m" played in a window. Rounds DOWN so a window never looks like it reached a tier
 *  it missed (14h 59.9m reads 14h 59m, not 15h 0m). */
const formatPlayedLabel = (hours: number): string => {
  const totalMinutes = Math.max(Math.floor((Number.isFinite(hours) ? hours : 0) * 60 + 1e-6), 0);
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
};

/** "Nov 8–14" (or "Nov 29–Dec 5" across a month) for a window starting on `weekKey`. */
const formatWeekRange = (weekKey: string): string => {
  const start = parseDateOnly(weekKey);
  if (!start) return '';
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const month = (date: Date) => date.toLocaleDateString(undefined, { month: 'short' });
  return start.getMonth() === end.getMonth()
    ? `${month(start)} ${start.getDate()}\u2013${end.getDate()}`
    : `${month(start)} ${start.getDate()}\u2013${month(end)} ${end.getDate()}`;
};

const sortedWeekEntries = (weeklyHours: Record<string, any> = {}): [string, any][] =>
  Object.entries(weeklyHours || {}).sort(([left], [right]) => left.localeCompare(right));

const isHybridWeekQualified = (weekData: any, weeklyTarget: number): boolean => {
  if (!weekData || weeklyTarget <= 0) return false;

  return Boolean(weekData.isAchieved) || getWeeklyHoursValue(weekData) >= weeklyTarget;
};

const getHybridFallbackWeekKey = (
  weeklyHours: Record<string, any>,
  totalTarget: number,
  totalFallbackQualified: boolean,
  currentWeek?: string
): string | null => {
  if (!totalFallbackQualified || totalTarget <= 0) return null;

  let runningTotal = 0;
  const weekEntries = sortedWeekEntries(weeklyHours);
  for (const [weekKey, weekData] of weekEntries) {
    runningTotal += getWeeklyHoursValue(weekData);
    if (runningTotal >= totalTarget) return weekKey;
  }

  return currentWeek || weekEntries[weekEntries.length - 1]?.[0] || null;
};

export function buildPromoDisplay(promo: Promotion): PromoDisplay {
  const rules = normalizeRules(promo);
  const format = resolveFormat(promo, rules);
  const breakdowns = (promo.hours as any)?.breakdowns || {};
  const thisWeekHours = toNumber(breakdowns.this_week);
  const totalHours = toNumber((promo.hours as any)?.total);
  const weeklyCharges = toNumber(breakdowns.weekly_charges);
  const totalRewards = toNumber(breakdowns.total_rewards);
  const resetDay = toNumber(rules.reset_day, 6);
  const normalizedResetDay = resetDay >= 0 && resetDay <= 6 ? resetDay : 6;
  const resetDayLabel = fullDayLabels[normalizedResetDay] || 'Saturday';
  const resetLabel = resetDayLabels[normalizedResetDay]
    ? `Resets ${resetDayLabels[normalizedResetDay]}`
    : '';
  const endLabel = promo.end_date ? `Ends ${formatDate(promo.end_date)}` : '';

  if (format === FORMAT_WEEKLY) {
    const weekConfig = getWeekConfig(promo, rules);
    const tiers = sortedTiers(
      weekConfig?.tiers || {},
      (chips) => `${compactNumber(toNumber(chips))} chips`
    ).map((tier) => ({ ...tier, reached: thisWeekHours >= tier.threshold }));
    const target = tiers[tiers.length - 1]?.threshold || toNumber(promo.goals?.weekly_hours_target);
    const highest = getHighestReached(tiers, thisWeekHours);
    const next = getNextTier(tiers, thisWeekHours);
    const tournamentDate = getConfiguredTournamentDate(promo, rules, weekConfig);

    return {
      format,
      formatLabel: formatLabels[format],
      metricLabel: "Current Week's Hours",
      progressLabel: `${hoursLabel(thisWeekHours)}${
        next ? ` / ${hoursLabel(next.threshold)}` : ''
      }`,
      progressValue: thisWeekHours,
      targetValue: target,
      progressPct: percent(thisWeekHours, target),
      earnedLabel:
        totalRewards > 0
          ? chipsLabel(totalRewards)
          : highest
          ? highest.valueLabel
          : 'Not qualified',
      statusLabel: highest ? 'Qualified this week' : 'Not qualified yet',
      resetDayLabel,
      resetLabel,
      endLabel,
      tiers,
      weekRows: getWeeklyRows(promo, rules, target),
      detailRows: [
        {
          label: 'Freeroll',
          value: weekConfig?.freeroll_tournament_id ? `#${weekConfig.freeroll_tournament_id}` : '-'
        },
        {
          label: 'Tournament Date',
          value: tournamentDate || '-'
        },
        {
          label: 'Chip Count',
          value:
            totalRewards > 0 ? chipsLabel(totalRewards) : highest ? highest.valueLabel : '0 chips'
        }
      ]
    };
  }

  if (format === FORMAT_TIER) {
    const tiers = sortedTiers(
      rules.tiers || {},
      (tournamentId) => `Tournament #${tournamentId}`
    ).map((tier) => {
      const meta = rules.tier_meta?.[String(tier.threshold)] || {};
      return {
        ...tier,
        valueLabel: meta.label || tier.valueLabel,
        reached: totalHours >= tier.threshold
      };
    });
    const target = tiers[tiers.length - 1]?.threshold || toNumber(promo.goals?.total_hours_target);
    const highest = getHighestReached(tiers, totalHours);
    const next = getNextTier(tiers, totalHours);
    const tournamentDate = formatTournamentDate(
      highest
        ? promo.reward?.tiers?.find((tier: any) => toNumber(tier.min_hours) === highest.threshold)
            ?.tournament_start_date
        : undefined
    );

    return {
      format,
      formatLabel: formatLabels[format],
      metricLabel: 'Total Hours',
      progressLabel: `${hoursLabel(totalHours)}${next ? ` / ${hoursLabel(next.threshold)}` : ''}`,
      progressValue: totalHours,
      targetValue: target,
      progressPct: percent(totalHours, target),
      earnedLabel: highest ? highest.valueLabel : 'Not qualified',
      statusLabel: highest ? 'Qualified for a tier' : 'Not qualified yet',
      resetDayLabel,
      resetLabel: '',
      endLabel,
      tiers,
      weekRows: [],
      detailRows: [
        { label: 'Best Tier', value: highest ? highest.valueLabel : 'None yet' },
        ...(tournamentDate ? [{ label: 'Tournament Date', value: tournamentDate }] : [])
      ]
    };
  }

  if (format === FORMAT_HYBRID) {
    const weeklyTarget = toNumber(rules.weekly_target_hours || promo.goals?.weekly_target_hours);
    const totalTarget = toNumber(rules.total_hours_target || promo.goals?.total_hours_target);
    const weeklyHours = breakdowns.weekly_hours || {};
    const currentWeek = (promo.hours as any)?.current_week || promo.goals?.current_week;
    const currentWeekData = currentWeek ? weeklyHours[currentWeek] : null;
    const currentWeekQualified =
      weeklyTarget > 0 &&
      (isHybridWeekQualified(currentWeekData, weeklyTarget) || thisWeekHours >= weeklyTarget);
    const weeklyQualifiedWeekKeys = sortedWeekEntries(weeklyHours)
      .filter(([, weekData]) => isHybridWeekQualified(weekData, weeklyTarget))
      .map(([weekKey]) => weekKey);
    const qualifiedWeekCount = weeklyQualifiedWeekKeys.length;
    const totalFallbackSignal = totalTarget > 0 && totalHours >= totalTarget;
    const fallbackCandidateWeekKey = getHybridFallbackWeekKey(
      weeklyHours,
      totalTarget,
      totalFallbackSignal,
      currentWeek
    );
    const weeklyQualificationBlocksFallback = fallbackCandidateWeekKey
      ? weeklyQualifiedWeekKeys.some((weekKey) => weekKey <= fallbackCandidateWeekKey)
      : false;
    const totalFallbackQualified = Boolean(
      fallbackCandidateWeekKey && !weeklyQualificationBlocksFallback
    );
    const fallbackWeekKey = totalFallbackQualified ? fallbackCandidateWeekKey : null;
    const weeklyBonusWeekKeys =
      totalFallbackQualified && fallbackWeekKey
        ? weeklyQualifiedWeekKeys.filter((weekKey) => weekKey > fallbackWeekKey)
        : [];
    const weeklyPathQualified = totalFallbackQualified
      ? weeklyBonusWeekKeys.length > 0
      : qualifiedWeekCount > 0 || currentWeekQualified;
    const entryQualifiedByWeeklyPath = !totalFallbackQualified && weeklyPathQualified;
    const totalFallbackEligible =
      totalTarget > 0 && !entryQualifiedByWeeklyPath && !totalFallbackQualified;
    const resolvedQualifiedWeekCount = entryQualifiedByWeeklyPath
      ? qualifiedWeekCount > 0
        ? qualifiedWeekCount
        : currentWeekQualified
        ? 1
        : 0
      : 0;
    const totalFallbackAvailable = totalFallbackEligible;
    const baseChips = toNumber(rules.base_starting_chips);
    const boosterChips = toNumber(rules.booster_chips_per_extra_qualified_week);
    const totalFallbackChips = totalFallbackQualified
      ? toNumber(rules.total_hours_starting_chips)
      : 0;
    const weeklyChips =
      resolvedQualifiedWeekCount > 0
        ? baseChips + Math.max(0, resolvedQualifiedWeekCount - 1) * boosterChips
        : 0;
    const fallbackBonusChips = totalFallbackQualified
      ? weeklyBonusWeekKeys.length * boosterChips
      : 0;
    const awardedChips = totalFallbackQualified
      ? totalFallbackChips + fallbackBonusChips
      : weeklyChips || totalRewards;
    const tournamentDate = getConfiguredTournamentDate(promo, rules);
    const tournamentDateShort = formatTournamentDateShort(
      rules.tournament_start_date ||
        rules.tournamentStartDate ||
        promo.reward?.tournament_start_date ||
        promo.reward?.weeks?.find(
          (week: any) => week.freeroll_tournament_id === rules.freeroll_tournament_id
        )?.tournament_start_date
    );
    const bonusAwarded = totalFallbackQualified
      ? fallbackBonusChips
      : Math.max(0, resolvedQualifiedWeekCount - 1) * boosterChips;
    let reachedWeekIndex = 0;
    const weekRows = getWeeklyRows(promo, rules, weeklyTarget).map((week) => {
      if (week.key === fallbackWeekKey) {
        return {
          ...week,
          rewardLabel: chipsLabel(totalFallbackChips || toNumber(rules.total_hours_starting_chips)),
          status: 'Qualified by total-hours fallback',
          reached: true
        };
      }

      if (fallbackWeekKey) {
        return week.reached && week.key > fallbackWeekKey
          ? {
              ...week,
              rewardLabel: chipsLabel(boosterChips)
            }
          : week;
      }

      if (!week.reached) return week;

      const rewardLabel = chipsLabel(reachedWeekIndex === 0 ? baseChips : boosterChips);
      reachedWeekIndex += 1;

      return {
        ...week,
        rewardLabel
      };
    });

    const currentWeekClosedByFallback = Boolean(
      totalFallbackQualified && fallbackWeekKey && fallbackWeekKey === currentWeek
    );
    // The fallback secures the entry, but the backend still credits this week only once
    // it reaches the weekly target, so the countdown always tracks the weekly figures.
    const remainingHours = Math.max(weeklyTarget - thisWeekHours, 0);
    const hybridState =
      awardedChips <= 0 ? 'notQualified' : bonusAwarded > 0 ? 'bonusAchieved' : 'qualified';
    const entryStatusKey =
      currentWeekQualified && !currentWeekClosedByFallback
        ? 'currentWeekQualified'
        : totalFallbackQualified
        ? 'totalFallbackQualified'
        : weeklyPathQualified
        ? 'weeklyPathQualified'
        : 'notQualified';

    return {
      format,
      formatLabel: formatLabels[format],
      metricLabel: "Current Week's Hours",
      progressLabel: `${hoursLabel(thisWeekHours)} / ${hoursLabel(weeklyTarget)}`,
      progressValue: thisWeekHours,
      targetValue: weeklyTarget,
      progressPct: percent(thisWeekHours, weeklyTarget),
      earnedLabel: awardedChips > 0 ? chipsLabel(awardedChips) : '',
      statusLabel: totalFallbackQualified
        ? 'Qualified by total-hours fallback'
        : currentWeekQualified
        ? 'Qualified this week'
        : weeklyPathQualified
        ? 'Qualified by weekly path'
        : 'Not qualified yet',
      resetDayLabel,
      resetLabel,
      endLabel,
      tiers: [
        {
          threshold: weeklyTarget,
          label: `${hoursLabel(weeklyTarget)} weekly`,
          valueLabel:
            boosterChips > 0
              ? `${chipsLabel(baseChips)} + ${chipsLabel(boosterChips)} / extra week`
              : chipsLabel(baseChips),
          reached: weeklyPathQualified
        },
        {
          threshold: totalTarget,
          label: `${hoursLabel(totalTarget)} total`,
          valueLabel: chipsLabel(totalFallbackChips || toNumber(rules.total_hours_starting_chips)),
          reached: totalFallbackQualified
        }
      ].filter((tier) => tier.threshold > 0),
      weekRows,
      detailRows: [
        {
          label: 'Total Chips Awarded',
          value: awardedChips > 0 ? chipsLabel(awardedChips) : '0 chips'
        },
        {
          label: 'Qualification Path',
          value: totalFallbackQualified
            ? 'Total-hours fallback'
            : weeklyPathQualified
            ? 'Weekly qualification'
            : 'Not qualified'
        },
        { label: 'Total Hours', value: `${hoursLabel(totalHours)} / ${hoursLabel(totalTarget)}` },
        {
          label: 'Freeroll',
          value: rules.freeroll_tournament_id ? `#${rules.freeroll_tournament_id}` : '-'
        },
        {
          label: 'Tournament Date',
          value: tournamentDate || '-'
        }
      ],
      hybridDetails: {
        state: hybridState,
        entryStatusKey,
        remainingLabel: formatMinutesClockLabel(toRemainingMinutes(remainingHours)),
        currentWeekHoursLabel: formatHoursClockLabel(thisWeekHours),
        weeklyTargetHoursLabel: hoursLabel(weeklyTarget).toUpperCase(),
        totalHoursLabel: hoursLabel(totalHours).toUpperCase(),
        totalTargetHoursLabel: hoursLabel(totalTarget).toUpperCase(),
        eligibleBlindsLabel: getEligibleBlindsLabel(rules),
        weekWindowLabel: getWeekWindowLabel(resetDay),
        resetDayLabel,
        tournamentDateLabel: tournamentDateShort || tournamentDate || '-',
        qualifyingDateRangeLabel: [
          formatShortDate(promo.start_date),
          formatShortDate(promo.end_date)
        ]
          .filter(Boolean)
          .join(' to '),
        seatLabel: rules.seat_label || rules.seatLabel || hoursLabel(weeklyTarget).toUpperCase(),
        startingStackLabel: formatStackLabel(
          totalFallbackQualified
            ? totalFallbackChips || toNumber(rules.total_hours_starting_chips)
            : baseChips
        ),
        bonusStackLabel: formatStackLabel(boosterChips),
        totalFallbackAvailable,
        totalFallbackQualified,
        currentWeekQualified,
        totalFallbackStackLabel: formatStackLabel(toNumber(rules.total_hours_starting_chips))
      }
    };
  }

  if (format === FORMAT_CASH_BACK) {
    // The window pays the highest tier reached; the bar and the remaining time both measure
    // the NEXT tier, so 14h54m never reads as done. The server sends those numbers in
    // `cash_back` (EN-11293); the rules are read only when a backend predates it.
    const progress = getCashBackTierProgress(promo, rules, thisWeekHours);
    const { tiers, highest, next } = progress;
    const weekHours = progress.hours;
    const target = tiers[tiers.length - 1]?.threshold || 0;
    const tierState: 'notQualified' | 'qualified' | 'bonusAchieved' = !highest
      ? 'notQualified'
      : highest.threshold >= target
      ? 'bonusAchieved'
      : 'qualified';
    // A player with no tracked hours yet gets no current_week from the server; the window
    // containing today (in CT) is the current one.
    const currentWeek =
      promo.cash_back?.week_key ||
      (promo.hours as any)?.current_week ||
      promo.goals?.current_week ||
      currentWeekStartKeyCT(normalizedResetDay, promo);
    const eligibleBlindsLabel = getEligibleBlindsLabel(rules);
    const weekWindowLabel = getWeekWindowLabel(normalizedResetDay);
    const cashBackWeekRows = getCashBackWeekRows(
      promo,
      tiers,
      currentWeek,
      normalizedResetDay,
      progress.weekResults,
      progress.scheduleFromPayload
    );
    // Each window pays its highest tier; the month's total is the sum across windows.
    const monthTotal = Object.values(progress.weekResults).reduce(
      (sum, result) => sum + (result.tier ? result.tier.amount : 0),
      0
    );

    return {
      format,
      formatLabel: formatLabels[format],
      metricLabel: 'Your Hours This Week',
      progressLabel: `${hoursLabel(weekHours)}${next ? ` / ${hoursLabel(next.threshold)}` : ''}`,
      progressValue: weekHours,
      // The bar measures the tier being chased, not the top tier: 7.6 h of 15 h reads half
      // full, and once $20 is earned it reads 15 h of 30 h (half full again). Full at the top.
      targetValue: next ? next.threshold : target,
      progressPct: percent(weekHours, next ? next.threshold : target),
      earnedLabel: highest ? highest.valueLabel : 'Not qualified',
      statusLabel:
        tierState === 'bonusAchieved'
          ? `${highest?.valueLabel} earned this week`
          : tierState === 'qualified'
          ? `${highest?.valueLabel} earned`
          : 'Not qualified yet',
      resetDayLabel,
      resetLabel,
      endLabel,
      tiers,
      weekRows: cashBackWeekRows,
      detailRows: [
        {
          label: 'Eligible Tables',
          value: hasNoMinimumBlind(rules) ? 'All cash games' : `${eligibleBlindsLabel} cash games`
        },
        {
          label: 'Weekly Window',
          value: `${resetDayLabels[normalizedResetDay]} 12:00 AM \u2013 ${
            resetDayLabels[(normalizedResetDay + 6) % 7]
          } 11:59 PM CT`
        }
      ],
      tierState,
      monthTotalLabel: monthTotal > 0 ? moneyLabel(monthTotal) : '',
      nextTierLabel:
        tierState === 'qualified' && next
          ? `Next: ${next.valueLabel} at ${hoursLabel(next.threshold)}`
          : '',
      // Top tier reached: the headline slot shows the cash earned instead of a zero countdown.
      remainingLabel: next
        ? formatHoursMinutesLabel(progress.hoursToNext)
        : highest?.valueLabel || '',
      eligibleBlindsLabel,
      weekWindowLabel,
      qualifyingDateRangeLabel: `${formatShortDate(promo.start_date)} to ${formatShortDate(
        promo.end_date
      )}`
    };
  }

  if (format === FORMAT_TIMEBACK) {
    const tiers = sortedEntries(rules.tiers || {}).map(([threshold, refund]) => ({
      threshold: toNumber(threshold),
      label: moneyLabel(toNumber(threshold)),
      valueLabel: `${toNumber(refund)}% back`,
      reached: weeklyCharges >= toNumber(threshold)
    }));
    const target = tiers[tiers.length - 1]?.threshold || 0;
    const highest = getHighestReached(tiers, weeklyCharges);
    const next = getNextTier(tiers, weeklyCharges);

    return {
      format,
      formatLabel: formatLabels[format],
      metricLabel: 'Weekly Net Fees',
      progressLabel: `${moneyLabel(weeklyCharges)}${
        next ? ` / ${moneyLabel(next.threshold)}` : ''
      }`,
      progressValue: weeklyCharges,
      targetValue: target,
      progressPct: percent(weeklyCharges, target),
      earnedLabel: highest ? highest.valueLabel : 'No timeback yet',
      statusLabel: highest ? 'Timeback tier reached' : 'Not qualified yet',
      resetDayLabel,
      resetLabel,
      endLabel,
      tiers,
      weekRows: [],
      detailRows: [{ label: 'Current Refund', value: highest ? highest.valueLabel : '0%' }]
    };
  }

  const legacyWeeklyTarget = toNumber(promo.goals?.weekly_hours_target);
  const legacyTotalTarget = toNumber(promo.goals?.total_hours_target);
  const target =
    legacyWeeklyTarget ||
    legacyTotalTarget ||
    toNumber((promo.hours as any)?.breakdowns?.total_countdown?.hours);

  return {
    format,
    formatLabel: formatLabels.legacy,
    metricLabel: legacyWeeklyTarget ? "Current Week's Hours" : 'Total Hours',
    progressLabel: `${hoursLabel(legacyWeeklyTarget ? thisWeekHours : totalHours)} / ${hoursLabel(
      target
    )}`,
    progressValue: legacyWeeklyTarget ? thisWeekHours : totalHours,
    targetValue: target,
    progressPct: percent(legacyWeeklyTarget ? thisWeekHours : totalHours, target),
    earnedLabel: promo.reward?.description || promo.reward?.type || 'Reward',
    statusLabel: 'In progress',
    resetDayLabel,
    resetLabel,
    endLabel,
    tiers: target
      ? [
          {
            threshold: target,
            label: hoursLabel(target),
            valueLabel: promo.reward?.description || 'Reward',
            reached: (legacyWeeklyTarget ? thisWeekHours : totalHours) >= target
          }
        ]
      : [],
    weekRows: getWeeklyRows(promo, rules, target),
    detailRows: [{ label: 'Reward', value: promo.reward?.description || promo.reward?.type || '-' }]
  };
}
